/*
 * Cottomix theme — <cm-bundle> (cm-bundle.js)
 *
 * Purpose: client-side bundle builder for the home bundle section. The
 * shopper picks N products (selection is global across tabs), the element
 * mirrors the selection into the summary slots, applies the configured
 * bundle discount to the displayed totals and adds every chosen variant to
 * the cart in a single request.
 *
 * Pure vanilla JS custom element, no build step. No visible string lives
 * here: every label and message is read from the data-* attributes the
 * section renders.
 *
 * Public attributes (on <cm-bundle>):
 *   data-size           how many products make a full bundle (default 2)
 *   data-discount-type  "none" | "percentage" | "fixed"
 *   data-discount-value discount number (percent, or currency units when fixed)
 *   data-currency       currency code/text appended to formatted amounts
 *   data-msg-progress   progress text with the tokens __CURRENT__ / __TOTAL__
 *   data-msg-remove     slot remove aria-label, token __TITLE__ = product title
 *   data-msg-full       status text when the bundle is already full
 *   data-msg-added      status text after a successful add
 *   data-msg-error      fallback status text when the add fails
 *
 * Markup contract (rendered by the bundle section):
 *   cards [data-cm-bundle-card] with data-product-id / data-variant-id
 *     (selected variant, source of truth) / data-title / data-image /
 *     data-url; inside: one <select data-cm-card-select data-position="n">
 *     per product option (label = translated option name), an optional
 *     qty stepper [data-cm-card-qty] with [data-cm-card-qty-minus] /
 *     [data-cm-card-qty-input] / [data-cm-card-qty-plus] (offer cards
 *     step in sets of 3 — QC-OPUS-R1 #28b), a note [data-cm-card-note]
 *     (offer wording) and stock line [data-cm-card-stock] with the
 *     data-msg-stock template (__COUNT__ token), a button [data-cm-pick]
 *     with data-label-add / data-label-remove, and a JSON script
 *     [data-cm-card-variants]: [{id, options[], available, price, compare,
 *     max}] — max = tracked inventory or null.
 *   one side image img[data-cm-side-img] (plain photo, QC-OPUS-R1 #32),
 *     per-tab panels [data-cm-side-img="url"][hidden] — the visible panel's
 *     URL is copied into the image unless it carries data-side-static.
 *   a fixed summary bar [data-cm-bar][hidden] > p[data-cm-bar-info]
 *     (role=status) + button[data-cm-add]: shown only once the first item
 *     is picked, while the section is in view and no drawer is open.
 *
 * Attributes used from the bar (on <cm-bundle>):
 *   data-msg-progress (with __CURRENT__ / __TOTAL__), data-msg-total
 *   ("Total"/«الإجمالي»), data-currency fallback when CMConfig.moneyFormat
 *   is missing.
 *
 * Events: uses CM.cart.add (already emits cart:updated) and emits
 * "cart:open" via CM.emit after a successful add so the drawer opens.
 */
(function () {
  'use strict';

  if (window.customElements && window.customElements.get('cm-bundle')) return;

  var OFFER_STEP = 3;
  var OFFER_MAX = 12;

  class CMBundle extends HTMLElement {
    constructor() {
      super();
      this.items = [];
      this.busy = false;
      this.size = 2;
      this.inView = true;
      this.variantsCache = new WeakMap();
      this.boundClick = this.handleClick.bind(this);
      this.boundChange = this.handleChange.bind(this);
      this.boundInput = this.handleInput.bind(this);
      this.boundBodyClass = this.handleBodyClass.bind(this);
      this.viewObserver = null;
      this.bodyObserver = null;
      this.panelObserver = null;
    }

    connectedCallback() {
      this.readConfig();
      this.addEventListener('click', this.boundClick);
      this.addEventListener('change', this.boundChange);
      this.addEventListener('input', this.boundInput);

      // Summary bar lives only while the section is on screen.
      if (typeof IntersectionObserver === 'function') {
        this.viewObserver = new IntersectionObserver(
          (entries) => {
            this.inView = entries.some(function (entry) {
              return entry.isIntersecting;
            });
            this.updateBar();
          },
          { threshold: 0 }
        );
        this.viewObserver.observe(this);
      } else {
        this.inView = true;
      }

      // The drawer / scroll-lock classes must push the bar away.
      if (document.body) {
        this.bodyObserver = new MutationObserver(this.boundBodyClass);
        this.bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        this.boundBodyClass();
      }

      // One plain side photo per active tab.
      this.panelObserver = new MutationObserver(() => this.updateSide());
      this.panelObserver.observe(this, { subtree: true, attributes: true, attributeFilter: ['hidden'] });
      this.updateSide();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.boundClick);
      this.removeEventListener('change', this.boundChange);
      this.removeEventListener('input', this.boundInput);
      if (this.viewObserver) this.viewObserver.disconnect();
      if (this.bodyObserver) this.bodyObserver.disconnect();
      if (this.panelObserver) this.panelObserver.disconnect();
    }

    readConfig() {
      const size = parseInt(this.getAttribute('data-size'), 10);
      this.size = size > 0 ? size : 2;

      this.bar = this.querySelector('[data-cm-bar]');
      this.barInfo = this.querySelector('[data-cm-bar-info]');
      this.addBtn = this.querySelector('[data-cm-add]');
      this.sideImg = this.querySelector('img[data-cm-side-img]');
    }

    /* ---- bar / side-image visibility -------------------------------- */

    drawerOpen() {
      const body = document.body;
      const root = document.documentElement;
      return !!(
        (body && body.classList.contains('drawer-open')) ||
        (root && root.classList.contains('cm-scroll-locked'))
      );
    }

    handleBodyClass() {
      this.updateBar();
    }

    updateBar() {
      if (!this.bar) return;
      const has = this.items.length > 0;
      const show = has && this.inView && !this.drawerOpen();
      this.bar.hidden = !show;
      if (!this.barInfo) return;
      if (!show) {
        this.barInfo.textContent = '';
        return;
      }
      const msg = (this.getAttribute('data-msg-progress') || '')
        .replace('__CURRENT__', () => String(this.items.length))
        .replace('__TOTAL__', () => String(this.size));
      const total = ' ' + this.money(this.discountedTotal());
      this.barInfo.textContent = msg + ' · ' + (this.getAttribute('data-msg-total') || '') + total;
    }

    updateSide() {
      if (!this.sideImg || this.sideImg.hasAttribute('data-side-static')) return;
      const panels = this.querySelectorAll('[data-cm-panel][data-cm-side-img]');
      let url = '';
      for (let i = 0; i < panels.length; i += 1) {
        if (!panels[i].hidden) {
          url = panels[i].getAttribute('data-cm-side-img') || '';
          break;
        }
      }
      if (url && this.sideImg.getAttribute('src') !== url) this.sideImg.setAttribute('src', url);
    }

    /* ---- events ---------------------------------------------------- */

    handleClick(event) {
      const target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      const pick = target.closest('[data-cm-pick]');
      if (pick) {
        this.handlePick(pick);
        return;
      }

      const minus = target.closest('[data-cm-card-qty-minus]');
      if (minus) {
        this.stepCardQty(minus.closest('[data-cm-bundle-card]'), -1);
        return;
      }

      const plus = target.closest('[data-cm-card-qty-plus]');
      if (plus) {
        this.stepCardQty(plus.closest('[data-cm-bundle-card]'), 1);
        return;
      }

      const packChip = target.closest('[data-pack-qty]');
      if (packChip) {
        this.handlePackChip(packChip);
        return;
      }

      if (target.closest('[data-cm-add]')) {
        this.handleAdd();
      }
    }

    handlePackChip(chip) {
      const packDiv = chip.closest('[data-cm-card-packs]');
      if (!packDiv) return;
      const card = packDiv.closest('[data-cm-bundle-card]');
      if (!card) return;

      const qty = parseInt(chip.getAttribute('data-pack-qty'), 10);
      if (isNaN(qty)) return;

      // Update aria-pressed on all siblings
      const chips = packDiv.querySelectorAll('[data-pack-qty]');
      for (let i = 0; i < chips.length; i += 1) {
        chips[i].setAttribute('aria-pressed', chips[i] === chip ? 'true' : 'false');
      }

      // Update hidden input
      const input = card.querySelector('[data-cm-packs-input]');
      if (input) input.value = String(qty);

      // Update the pack totals from the per-piece prices (cents)
      const unit = Number(packDiv.getAttribute('data-unit-price')) || 0;
      const was = Number(packDiv.getAttribute('data-unit-compare')) || 0;
      const label = packDiv.getAttribute('data-currency-label') || '';
      const fmt = (cents) => this.number(cents / 100) + (label ? ' ' + label : '');
      const totalEl = packDiv.querySelector('[data-cm-packs-total]');
      if (totalEl) totalEl.textContent = fmt(unit * qty);
      const wasEl = packDiv.querySelector('[data-cm-packs-was]');
      if (wasEl && was > unit) wasEl.textContent = fmt(was * qty);
      const saveEl = packDiv.querySelector('[data-cm-packs-save]');
      if (saveEl && was > unit) {
        const prefix = document.documentElement.lang === 'ar' ? 'وفّر ' : 'Save ';
        saveEl.textContent = prefix + fmt((was - unit) * qty);
      }

      // Sync the card data
      this.syncCardItem(card);
    }

    handleInput(event) {
      const target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      if (!target.matches('[data-cm-card-qty-input]')) return;
      const raw = String(target.value).trim();
      if (raw === '' || !/^\d+$/.test(raw)) return; // still typing
      const card = target.closest('[data-cm-bundle-card]');
      const cfg = this.cardQtyConfig(card);
      let value = parseInt(raw, 10);
      if (!isNaN(value)) {
        // Snap to the configured set multiple, clamped to [min, max] —
        // there is no "type 0 removes" path on a bundle card (removal is
        // the pick toggle), so 0 snaps back to the minimum.
        if (cfg.step > 1) value = Math.round(value / cfg.step) * cfg.step;
        value = Math.min(cfg.max, Math.max(cfg.min, value));
        target.value = String(value);
        this.syncCardItem(card);
      }
    }

    handleChange(event) {
      const target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      if (!target.matches('[data-cm-card-select]')) return;

      const card = target.closest('[data-cm-bundle-card]');
      if (!card) return;

      this.resolveCardVariant(card, target);
    }

    /* ---- card variant resolution ------------------------------------ */

    cardVariants(card) {
      if (!card) return [];
      if (this.variantsCache.has(card)) return this.variantsCache.get(card);
      let variants = [];
      const script = card.querySelector('[data-cm-card-variants]');
      if (script) {
        try {
          variants = JSON.parse(script.textContent) || [];
        } catch (err) {
          variants = [];
        }
      }
      if (!Array.isArray(variants)) variants = [];
      this.variantsCache.set(card, variants);
      return variants;
    }

    cardVariant(card) {
      const id = String(card.getAttribute('data-variant-id') || '');
      const variants = this.cardVariants(card);
      for (let i = 0; i < variants.length; i += 1) {
        if (String(variants[i].id) === id) return variants[i];
      }
      return null;
    }

    sameOptions(a, b) {
      if (!Array.isArray(a) || a.length !== b.length) return false;
      for (let i = 0; i < a.length; i += 1) {
        if (String(a[i]) !== String(b[i])) return false;
      }
      return true;
    }

    /* Resolve the variant for the card after `changed` select moved.
       Exact match first; otherwise the first AVAILABLE variant sharing the
       changed value (its other options are synced back into the selects —
       same rule as the PDP picker). */
    resolveCardVariant(card, changed) {
      const variants = this.cardVariants(card);
      if (!variants.length) return;

      const selects = Array.prototype.slice.call(
        card.querySelectorAll('[data-cm-card-select]')
      );
      const values = selects.map(function (s) {
        return s.value;
      });

      /* CRO item 13: a card with a real size choice opens with no size selected. While none is
         chosen, a colour change only updates the card price / sold-out cues and never picks a
         size; choosing a size lifts the gate and unlocks the pick button. */
      if (card.hasAttribute('data-needs-size')) {
        const sizeSelect = card.querySelector('[data-cm-card-select][data-kind="size"]');
        if (sizeSelect && !sizeSelect.value) {
          let rep = null;
          for (let i = 0; i < variants.length; i += 1) {
            const cand = variants[i];
            if (!cand || !Array.isArray(cand.options)) continue;
            let ok = true;
            for (let j = 0; j < values.length; j += 1) {
              if (values[j] === '') continue;
              if (String(cand.options[j]) !== String(values[j])) {
                ok = false;
                break;
              }
            }
            if (!ok) continue;
            if (!rep) rep = cand;
            if (cand.available) {
              rep = cand;
              break;
            }
          }
          if (rep) {
            card.setAttribute('data-variant-id', String(rep.id));
            this.applyCardPrice(card, rep);
          }
          this.refreshSelectAvailability(card, selects);
          return;
        }
        card.removeAttribute('data-needs-size');
        const pickBtn = card.querySelector('[data-cm-pick]');
        if (pickBtn) {
          pickBtn.disabled = false;
          pickBtn.removeAttribute('aria-disabled');
          const addLabel = pickBtn.getAttribute('data-label-add');
          if (addLabel !== null) pickBtn.textContent = addLabel;
        }
      }

      let match = null;
      for (let i = 0; i < variants.length; i += 1) {
        if (variants[i].available && this.sameOptions(variants[i].options, values)) {
          match = variants[i];
          break;
        }
      }

      if (!match) {
        const pos = Number(changed.getAttribute('data-position')) - 1;
        for (let i = 0; i < variants.length; i += 1) {
          const cand = variants[i];
          if (
            cand.available &&
            Array.isArray(cand.options) &&
            String(cand.options[pos]) === String(changed.value)
          ) {
            match = cand;
            break;
          }
        }
        if (match) {
          for (let i = 0; i < selects.length; i += 1) {
            const p = Number(selects[i].getAttribute('data-position')) - 1;
            if (p !== pos && match.options[p] != null) selects[i].value = String(match.options[p]);
          }
        }
      }

      if (!match) return;

      card.setAttribute('data-variant-id', String(match.id));
      this.applyCardPrice(card, match);
      this.applyCardQty(card, match);
      this.refreshSelectAvailability(card, selects);
      this.syncCardItem(card);
      this.clearStatus();
      this.render();
    }

    /* A value stays selectable when SOME available variant carries it with
       all other positions equal to the current selection; otherwise it is
       disabled (sold out for this combination). */
    refreshSelectAvailability(card, selects) {
      const variants = this.cardVariants(card);
      const current = selects.map(function (s) {
        return s.value;
      });
      for (let i = 0; i < selects.length; i += 1) {
        const pos = Number(selects[i].getAttribute('data-position')) - 1;
        const options = selects[i].options;
        for (let j = 0; j < options.length; j += 1) {
          const value = options[j].value;
          let ok = false;
          for (let k = 0; k < variants.length; k += 1) {
            const cand = variants[k];
            if (!cand.available || !Array.isArray(cand.options)) continue;
            if (String(cand.options[pos]) !== String(value)) continue;
            let same = true;
            for (let m = 0; m < current.length; m += 1) {
              if (m === pos) continue;
              if (current[m] === '') continue; /* size not chosen yet (size gate): matches any */
              if (String(cand.options[m]) !== String(current[m])) {
                same = false;
                break;
              }
            }
            if (same) {
              ok = true;
              break;
            }
          }
          options[j].disabled = !ok;
        }
      }
    }

    applyCardPrice(card, variant) {
      const price = Number(variant.price) || 0;
      const compare = Number(variant.compare) || 0;
      const hasCompare = compare > 0 && compare > price;
      const cardPrice = card.querySelector('.cm-price__current [dir="ltr"]');
      if (cardPrice) cardPrice.textContent = this.number(price / 100);
      const cardCompare = card.querySelector('.cm-price__compare');
      if (cardCompare) cardCompare.hidden = !hasCompare;
      const cardSavings = card.querySelector('.cm-price__savings');
      if (cardSavings) cardSavings.hidden = !hasCompare;
    }

    /* ---- card quantity (offer cards: sets of 3, QC-OPUS-R1 #28b) ----- */

    cardQtyConfig(card) {
      const input = card && card.querySelector('[data-cm-card-qty-input]');
      if (!input) return null;
      const variant = this.cardVariant(card);
      /* while the size gate is on the stock cap is unknown (the chosen size sets it) */
      const invMax = !card.hasAttribute('data-needs-size') && variant && variant.max != null ? Number(variant.max) : null;
      let min = 1;
      let step = 1;
      let max = invMax;
      if (invMax == null || invMax >= OFFER_STEP) {
        step = OFFER_STEP;
        min = OFFER_STEP;
        max = invMax == null ? OFFER_MAX : Math.min(OFFER_MAX, Math.floor(invMax / OFFER_STEP) * OFFER_STEP);
      }
      return { min: min, step: step, max: max };
    }

    applyCardQty(card, variant) {
      const input = card.querySelector('[data-cm-card-qty-input]');
      if (!input) return;
      const invMax = variant && variant.max != null ? Number(variant.max) : null;
      let min = 1;
      let step = 1;
      let max = invMax;
      if (invMax == null || invMax >= OFFER_STEP) {
        step = OFFER_STEP;
        min = OFFER_STEP;
        max = invMax == null ? OFFER_MAX : Math.min(OFFER_MAX, Math.floor(invMax / OFFER_STEP) * OFFER_STEP);
      }
      input.setAttribute('min', String(min));
      input.setAttribute('step', String(step));
      if (max == null) input.removeAttribute('max');
      else input.setAttribute('max', String(max));

      let value = parseInt(input.value, 10);
      if (isNaN(value) || value < min) value = min;
      if (step > 1) value = Math.round(value / step) * step; // keep the set multiple
      if (value > max) value = max;
      if (value < min) value = min;
      input.value = String(value);

      const note = card.querySelector('[data-cm-card-note]');
      if (note) note.hidden = !(step === OFFER_STEP);
      const stock = card.querySelector('[data-cm-card-stock]');
      if (stock) {
        const lowAt = Number(stock.getAttribute('data-threshold')) || 5; /* QC #31: one low-stock threshold */
        if (invMax != null && invMax > 0 && invMax <= lowAt) {
          stock.textContent = (stock.getAttribute('data-msg-stock') || '').split('__COUNT__').join(String(invMax));
          stock.hidden = false;
        } else {
          stock.hidden = true;
          stock.textContent = '';
        }
      }
    }

    stepCardQty(card, direction) {
      const input = card && card.querySelector('[data-cm-card-qty-input]');
      if (!input) return;
      const cfg = this.cardQtyConfig(card);
      const step = cfg ? cfg.step : 1;
      const min = cfg ? cfg.min : 1;
      const max = cfg ? cfg.max : readNumericMax(input);
      let value = parseInt(input.value, 10);
      if (isNaN(value)) value = min;
      value =
        direction > 0
          ? Math.min(max, (Math.floor(value / step) + 1) * step)
          : Math.max(min, (Math.ceil(value / step) - 1) * step);
      input.value = String(value);
      this.syncCardItem(card);
    }

    /* Keep a already-picked item in sync with the card's current variant
       and quantity (no re-render — the caller decides). */
    syncCardItem(card) {
      const index = this.indexOfProduct(card.getAttribute('data-product-id'));
      if (index === -1) return;
      const variant = this.cardVariant(card);
      if (variant) {
        this.items[index].variantId = String(variant.id);
        this.items[index].price = Number(variant.price) || 0;
        this.items[index].compare = Number(variant.compare) || 0;
      }
      let qty = 1;
      const qtyInput = card.querySelector('[data-cm-card-qty-input]');
      if (qtyInput) {
        qty = parseInt(qtyInput.value, 10);
      } else {
        const packsInput = card.querySelector('[data-cm-packs-input]');
        if (packsInput) {
          qty = parseInt(packsInput.value, 10);
        }
      }
      this.items[index].qty = !isNaN(qty) && qty > 0 ? qty : 1;
    }

    /* ---- selection --------------------------------------------------- */

    handlePick(pick) {
      const card = pick.closest('[data-cm-bundle-card]');
      if (!card) return;
      if (card.hasAttribute('data-needs-size')) return; /* a size must be picked first */

      /* Pack cards (underwear / socks tabs): the pick button adds the chosen pack
         (3 / 6 pieces, or the product's own minimum) straight to the cart. */
      if (card.querySelector('[data-cm-card-packs]')) {
        this.addPack(card, pick);
        return;
      }

      const productId = card.getAttribute('data-product-id');
      const existing = this.indexOfProduct(productId);

      // Already selected -> remove it (toggle).
      if (existing !== -1) {
        this.items.splice(existing, 1);
        this.clearStatus();
        this.render();
        return;
      }

      // Bundle is full -> report and do nothing else.
      if (this.items.length >= this.size) {
        this.setStatus(this.getAttribute('data-msg-full'));
        return;
      }

      const variant = this.cardVariant(card);
      if (!variant || !variant.available) return; // no selectable variant -> ignore

      let qty = 1;
      const qtyInput = card.querySelector('[data-cm-card-qty-input]');
      if (qtyInput) {
        qty = parseInt(qtyInput.value, 10);
      } else {
        const packsInput = card.querySelector('[data-cm-packs-input]');
        if (packsInput) {
          qty = parseInt(packsInput.value, 10);
        }
      }
      if (isNaN(qty) || qty < 1) qty = 1;

      this.items.push({
        productId: productId,
        variantId: String(variant.id),
        title: card.getAttribute('data-title') || '',
        image: card.getAttribute('data-image') || '',
        url: card.getAttribute('data-url') || '',
        price: Number(variant.price) || 0,
        compare: Number(variant.compare) || 0,
        qty: qty
      });

      this.clearStatus();
      this.render();
    }

    addPack(card, pick) {
      if (this.busy) return;
      const variant = this.cardVariant(card);
      if (!variant || !variant.available) return;
      const packsInput = card.querySelector('[data-cm-packs-input]');
      let qty = packsInput ? parseInt(packsInput.value, 10) : 1;
      if (isNaN(qty) || qty < 1) qty = 1;
      const payload = [{ id: Number(variant.id), quantity: qty }];
      this.busy = true;
      pick.classList.add('is-loading');
      pick.disabled = true;
      const request = window.CM && CM.cart && typeof CM.cart.add === 'function'
        ? CM.cart.add(payload)
        : window.fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ items: payload })
          }).then(function (response) {
            if (!response.ok) throw new Error(response.statusText || 'request failed');
            return response.json();
          });
      const done = () => {
        this.busy = false;
        pick.classList.remove('is-loading');
        pick.disabled = false;
      };
      request
        .then(() => {
          this.setStatus(this.getAttribute('data-msg-added'));
          if (window.CM && typeof CM.emit === 'function') CM.emit('cart:open');
        })
        .catch((err) => {
          this.setStatus((err && err.message) || this.getAttribute('data-msg-error'));
        })
        .then(done, done);
    }

    /* ---- add to cart ----------------------------------------------- */

    handleAdd() {
      if (this.busy || this.items.length !== this.size) return;

      this.busy = true;
      if (this.addBtn) {
        this.addBtn.classList.add('is-loading');
        this.addBtn.disabled = true;
      }

      const payload = this.items.map(function (item) {
        return { id: Number(item.variantId), quantity: item.qty || 1 };
      });

      let request;
      if (window.CM && CM.cart && typeof CM.cart.add === 'function') {
        request = CM.cart.add(payload);
      } else {
        request = window
          .fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ items: payload })
          })
          .then(function (response) {
            if (!response.ok) throw new Error(response.statusText || 'request failed');
            return response.json();
          });
      }

      const done = () => {
        this.busy = false;
        if (this.addBtn) this.addBtn.classList.remove('is-loading');
        this.render();
      };

      request
        .then(() => {
          this.items = [];
          this.render();
          this.setStatus(this.getAttribute('data-msg-added'));
          if (window.CM && typeof CM.emit === 'function') CM.emit('cart:open');
        })
        .catch((err) => {
          this.setStatus((err && err.message) || this.getAttribute('data-msg-error'));
        })
        .then(done, done);
    }

    /* ---- rendering -------------------------------------------------- */

    render() {
      this.renderCards();
      this.updateBar();

      const full = this.items.length === this.size;
      if (this.addBtn) this.addBtn.disabled = !full || this.busy;
    }

    renderCards() {
      const cards = this.querySelectorAll('[data-cm-bundle-card]');
      Array.prototype.forEach.call(cards, (card) => {
        const selected = this.indexOfProduct(card.getAttribute('data-product-id')) !== -1;
        const pick = card.querySelector('[data-cm-pick]');
        if (pick) {
          pick.setAttribute('aria-pressed', selected ? 'true' : 'false');
          const gated = card.hasAttribute('data-needs-size');
          const isPack = !!card.querySelector('[data-cm-card-packs]');
          const packLabel = document.documentElement.lang === 'ar' ? 'أضف الباكيدج للسلة' : 'Add pack to cart';
          const label = gated
            ? pick.getAttribute('data-label-select-size')
            : isPack ? packLabel : pick.getAttribute(selected ? 'data-label-remove' : 'data-label-add');
          if (label !== null) pick.textContent = label;
        }
        card.classList.toggle('is-selected', selected);
      });
    }

    /* Current bar total in cents: bundle discount applies once the bundle
       is full (same rule the old summary panel used). */
    discountedTotal() {
      let sum = 0;
      for (let i = 0; i < this.items.length; i++) {
        sum += (Number(this.items[i].price) || 0) * (this.items[i].qty || 1);
      }

      const type = this.getAttribute('data-discount-type') || 'none';
      const value = parseFloat(this.getAttribute('data-discount-value')) || 0;

      if (this.items.length === this.size && value > 0) {
        if (type === 'percentage') {
          return Math.max(0, Math.round((sum * (100 - value)) / 100));
        }
        if (type === 'fixed') {
          return Math.max(0, sum - value * 100);
        }
      }
      return sum;
    }

    /* ---- helpers ---------------------------------------------------- */

    indexOfProduct(productId) {
      const id = String(productId === null || productId === undefined ? '' : productId);
      for (let i = 0; i < this.items.length; i++) {
        if (String(this.items[i].productId) === id) return i;
      }
      return -1;
    }

    setStatus(message) {
      // The bar is the only status surface left (QC-OPUS-R1 #32).
      if (this.barInfo) this.barInfo.textContent = message || '';
    }

    clearStatus() {
      if (this.barInfo) this.barInfo.textContent = '';
    }

    number(units) {
      let formatted;
      try {
        formatted = new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(units);
      } catch (e) {
        formatted = units.toFixed(2);
      }
      return formatted;
    }

    /* Plain number with a fixed digit count, no grouping (Shopify
       amount_no_decimals style tokens). */
    numberFixed(units, decimals) {
      const n = Number(units) || 0;
      return (Math.round(n * 100) / 100).toFixed(decimals);
    }

    /* Money for the bar total, ALWAYS with its currency (QC-OPUS-R1 #32).
       Primary: number + localized unit (data-currency = general.currency →
       EGP / ج.م) — the exact presentation of the price snippet every card
       on the page uses, so the bar never looks foreign next to them.
       Fallback: the shop money_format (standard tokens, HTML stripped so
       textContent stays text). */
    money(cents) {
      const safeCents = Number(cents) || 0;
      const unit = this.getAttribute('data-currency') || '';
      if (unit) return this.number(safeCents / 100) + ' ' + unit;

      const fmt = window.CMConfig && typeof CMConfig.moneyFormat === 'string' ? CMConfig.moneyFormat : '';
      if (fmt.indexOf('{{') !== -1) {
        const plain = fmt.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
        const out = plain.replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (m, token) => {
          const units = safeCents / 100;
          switch (token) {
            case 'amount':
              return this.number(units);
            case 'amount_no_decimals':
              return this.numberFixed(units, 0);
            case 'amount_with_comma_separator':
            case 'amount_with_apostrophe_separator':
              return this.number(units).replace(/,/g, '.');
            case 'amount_no_decimals_with_comma_separator':
              return this.numberFixed(units, 0).replace(/,/g, '.');
            default:
              return this.number(units);
          }
        });
        if (out) return out;
      }
      return this.number(safeCents / 100);
    }
  }

  function readNumericMax(input) {
    const raw = input.getAttribute('max');
    const num = raw == null || raw === '' ? NaN : Number(raw);
    return isNaN(num) ? Infinity : num;
  }

  customElements.define('cm-bundle', CMBundle);
})();
